import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../admin/context/AuthContext';
import { getOrderTotals, getItemLineTotal } from '../admin/utils/orderTotals';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const MyOrders = () => {
    const navigate = useNavigate();
    const { user, isAuthenticated, loading: authLoading } = useAuth();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);

    useEffect(() => {
        if (!authLoading && !isAuthenticated) {
            navigate('/account');
            return;
        }
        
        if (isAuthenticated && user) {
            fetchOrders();
        }
    }, [isAuthenticated, authLoading, user, navigate]);

    const fetchOrders = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE_URL}/client/orders`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Erreur lors du chargement des commandes');
            }

            const data = await response.json();
            setOrders(data.orders || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'pending': { label: 'En attente', class: 'bg-warning-100 text-warning-600' },
            'confirmed': { label: 'Confirmée', class: 'bg-info-100 text-info-600' },
            'processing': { label: 'En traitement', class: 'bg-primary-100 text-primary-600' },
            'shipped': { label: 'Expédiée', class: 'bg-purple-100 text-purple-600' },
            'delivered': { label: 'Livrée', class: 'bg-success-100 text-success-600' },
            'cancelled': { label: 'Annulée', class: 'bg-danger-100 text-danger-600' }
        };
        return statusMap[status] || { label: status, class: 'bg-gray-100 text-gray-600' };
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatPrice = (price) => {
        return parseFloat(price).toFixed(2) + ' DH';
    };

    const handleLogout = () => {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('admin_user');
        navigate('/');
    };

    if (authLoading || loading) {
        return (
            <section className="py-80">
                <div className="container container-lg">
                    <div className="text-center py-48">
                        <div className="spinner-border text-main-600" role="status">
                            <span className="visually-hidden">Chargement...</span>
                        </div>
                        <p className="mt-16 text-gray-600">Chargement de vos commandes...</p>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="my-orders py-80">
            <div className="container container-lg">
                <div className="row g-4">
                    {/* Sidebar */}
                    <div className="col-lg-3">
                        <div className="border border-gray-100 rounded-16 p-24 h-100">
                            <div className="text-center mb-24 pb-24 border-bottom border-gray-100">
                                <div className="w-80 h-80 bg-main-100 rounded-circle flex-center mx-auto mb-16">
                                    <span className="text-main-600 text-2xl fw-bold">
                                        {user?.name?.charAt(0).toUpperCase() || 'U'}
                                    </span>
                                </div>
                                <h6 className="mb-4">{user?.name}</h6>
                                <p className="text-gray-500 text-sm">{user?.email}</p>
                            </div>
                            
                            <nav className="d-flex flex-column gap-8">
                                <Link 
                                    to="/my-orders" 
                                    className="d-flex align-items-center gap-12 p-12 rounded-8 bg-main-50 text-main-600"
                                >
                                    <i className="ph ph-package text-xl"></i>
                                    <span>Mes Commandes</span>
                                </Link>
                                <Link 
                                    to="/track-order" 
                                    className="d-flex align-items-center gap-12 p-12 rounded-8 text-gray-600 hover-bg-gray-50"
                                >
                                    <i className="ph ph-map-pin text-xl"></i>
                                    <span>Suivre Commande</span>
                                </Link>
                                <button 
                                    onClick={handleLogout}
                                    className="d-flex align-items-center gap-12 p-12 rounded-8 text-danger-600 hover-bg-danger-50 border-0 bg-transparent w-100 text-start"
                                >
                                    <i className="ph ph-sign-out text-xl"></i>
                                    <span>Déconnexion</span>
                                </button>
                            </nav>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="col-lg-9">
                        <div className="border border-gray-100 rounded-16 p-32">
                            <div className="d-flex align-items-center justify-content-between mb-32 pb-24 border-bottom border-gray-100">
                                <div>
                                    <h4 className="mb-8">Mes Commandes</h4>
                                    <p className="text-gray-500 mb-0">Historique de toutes vos commandes</p>
                                </div>
                                <span className="badge bg-main-100 text-main-600 px-16 py-8 rounded-pill">
                                    {orders.length} commande{orders.length > 1 ? 's' : ''}
                                </span>
                            </div>

                            {error && (
                                <div className="alert alert-danger rounded-12 mb-24">
                                    <i className="ph ph-warning me-8"></i>
                                    {error}
                                </div>
                            )}

                            {orders.length === 0 ? (
                                <div className="text-center py-48">
                                    <div className="w-120 h-120 bg-gray-50 rounded-circle flex-center mx-auto mb-24">
                                        <i className="ph ph-package text-gray-400" style={{ fontSize: '48px' }}></i>
                                    </div>
                                    <h5 className="mb-12 text-gray-700">Aucune commande</h5>
                                    <p className="text-gray-500 mb-24">Vous n'avez pas encore passé de commande.</p>
                                    <Link to="/shop" className="btn btn-main py-12 px-32 rounded-pill">
                                        <i className="ph ph-shopping-cart me-8"></i>
                                        Commencer mes achats
                                    </Link>
                                </div>
                            ) : (
                                <div className="d-flex flex-column gap-16">
                                    {orders.map((order) => {
                                        const status = getStatusBadge(order.status);
                                        return (
                                            <div 
                                                key={order.id} 
                                                className="border border-gray-100 rounded-12 p-20 hover-border-main-200 transition-1 cursor-pointer"
                                                onClick={() => setSelectedOrder(selectedOrder?.id === order.id ? null : order)}
                                            >
                                                <div className="d-flex align-items-center justify-content-between flex-wrap gap-16">
                                                    <div className="d-flex align-items-center gap-16">
                                                        <div className="w-48 h-48 bg-main-50 rounded-12 flex-center">
                                                            <i className="ph ph-package text-main-600 text-xl"></i>
                                                        </div>
                                                        <div>
                                                            <h6 className="mb-4 text-gray-900">
                                                                {order.order_number || order.serial_name}
                                                            </h6>
                                                            <p className="text-gray-500 text-sm mb-0">
                                                                {formatDate(order.created_at)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="d-flex align-items-center gap-24">
                                                        <div className="text-end">
                                                            <p className="text-gray-500 text-sm mb-0">Total</p>
                                                            <p className="fw-semibold text-gray-900 mb-0">
                                                                {formatPrice(getOrderTotals(order).grandTotal)}
                                                            </p>
                                                        </div>
                                                        <span className={`badge ${status.class} px-12 py-6 rounded-pill`}>
                                                            {status.label}
                                                        </span>
                                                        <i className={`ph ph-caret-${selectedOrder?.id === order.id ? 'up' : 'down'} text-gray-400`}></i>
                                                    </div>
                                                </div>

                                                {/* Expanded Order Details */}
                                                {selectedOrder?.id === order.id && (
                                                    <div className="mt-20 pt-20 border-top border-gray-100">
                                                        <div className="row g-4">
                                                            {/* Order Items */}
                                                            <div className="col-md-8">
                                                                <h6 className="text-sm text-gray-500 mb-12">Articles commandés</h6>
                                                                <div className="d-flex flex-column gap-12">
                                                                    {order.items?.map((item, idx) => {
                                                                        // Build image URL with proper /storage/ prefix handling
                                                                        const getItemImageUrl = (imagePath) => {
                                                                            if (!imagePath) return null;
                                                                            if (imagePath.startsWith('http')) return imagePath;
                                                                            const baseUrl = API_BASE_URL.replace('/api', '');
                                                                            if (imagePath.startsWith('/storage/')) return `${baseUrl}${imagePath}`;
                                                                            if (imagePath.startsWith('storage/')) return `${baseUrl}/${imagePath}`;
                                                                            return `${baseUrl}/storage/${imagePath}`;
                                                                        };
                                                                        return (
                                                                        <div key={idx} className="d-flex align-items-center gap-12">
                                                                            <div className="w-48 h-48 bg-gray-50 rounded-8 flex-center overflow-hidden">
                                                                                {item.product?.image ? (
                                                                                    <img 
                                                                                        src={getItemImageUrl(item.product.image)} 
                                                                                        alt={item.product_name}
                                                                                        className="w-100 h-100 object-fit-cover"
                                                                                    />
                                                                                ) : (
                                                                                    <i className="ph ph-image text-gray-300"></i>
                                                                                )}
                                                                            </div>
                                                                            <div className="flex-grow-1">
                                                                                <p className="mb-0 fw-medium">{item.product_name}</p>
                                                                                <p className="text-gray-500 text-sm mb-0">
                                                                                    {item.quantity} x {formatPrice(item.price)}
                                                                                </p>
                                                                            </div>
                                                                            <span className="fw-semibold">
                                                                                {formatPrice(getItemLineTotal(item))}
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                    })}
                                                                </div>
                                                            </div>

                                                            {/* Delivery Info */}
                                                            <div className="col-md-4">
                                                                <h6 className="text-sm text-gray-500 mb-12">Livraison</h6>
                                                                <div className="bg-gray-50 rounded-8 p-16">
                                                                    <p className="mb-8">
                                                                        <i className="ph ph-map-pin text-main-600 me-8"></i>
                                                                        {order.address}
                                                                    </p>
                                                                    {order.city && (
                                                                        <p className="mb-8 text-gray-600">{order.city}</p>
                                                                    )}
                                                                    <p className="mb-0">
                                                                        <i className="ph ph-phone text-main-600 me-8"></i>
                                                                        {order.phone}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Order Summary */}
                                                        <div className="d-flex justify-content-end mt-20 pt-16 border-top border-gray-100">
                                                            {(() => {
                                                                const totals = getOrderTotals(order);
                                                                return (
                                                            <div className="text-end">
                                                                <div className="d-flex justify-content-between gap-32 mb-8">
                                                                    <span className="text-gray-500">Sous-total:</span>
                                                                    <span>{formatPrice(totals.subtotal)}</span>
                                                                </div>
                                                                <div className="d-flex justify-content-between gap-32 mb-8">
                                                                    <span className="text-gray-500">TVA (20%):</span>
                                                                    <span>{formatPrice(totals.tax)}</span>
                                                                </div>
                                                                <div className="d-flex justify-content-between gap-32 mb-8">
                                                                    <span className="text-gray-500">Livraison:</span>
                                                                    <span>{formatPrice(totals.shipping)}</span>
                                                                </div>
                                                                <div className="d-flex justify-content-between gap-32 fw-bold text-lg">
                                                                    <span>Total:</span>
                                                                    <span className="text-main-600">{formatPrice(totals.grandTotal)}</span>
                                                                </div>
                                                            </div>
                                                                );
                                                            })()}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default MyOrders;
